import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { useStyles } from "./styles";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import QRCode from "react-qr-code";
import useFormHandlers from "../useFormHandlers";
import { useFormik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import SettingsResetButton from "../SettingsResetButton";
import { useSettingDispatchChange } from "../index";
import { useEnqueueSnackbar } from "../../../../hooks/useEnqueueSnackbar";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlags";
import ReceiptLines from "./ReceiptLines";

const PrinterSettingsValidation = Yup.object().shape({
  // printerretracttimeout: Yup.number()
  //   .typeError("Must be a number")
  //   .integer("Must be a whole number")
  //   .positive("Must be greater than zero")
  //   .required("Required"),
  autoprintreceipt: Yup.string().required(),
  receipt_header: Yup.string()
    .max(36, "Each Line Should be a Max of 36 Characters")
    .optional(),
  receipt_footer: Yup.string()
    .max(36, "Each Line Should be a Max of 36 Characters")
    .optional(),
  receiptHeaderLines: Yup.array()
    .of(Yup.string().max(36, "Each Line Should be a Max of 36 Characters"))
    .optional(),
  receiptFooterLines: Yup.array()
    .of(Yup.string().max(36, "Each Line Should be a Max of 36 Characters"))
    .optional(),
});

const PrinterSettingsForm = ({
  entityID,
  entityType,
  settingValues,
  settingValuesParent,
  parentEntityID,
  parentEntityType,
}) => {
  const classes = useStyles();
  const [printerExpanded, setPrinterExpanded] = useState(true);
  const [receiptHeaderLines, setReceiptHeaderLines] = useState(
    settingValues.receipt_header?.split("\n")
  );
  const [receiptFooterLines, setReceiptFooterLines] = useState(
    settingValues.receipt_footer?.split("\n")
  );
  const { dispatchSetting } = useSettingDispatchChange();
  const enqueueSnackbar = useEnqueueSnackbar();
  const { values, errors, setFieldValue, initialValues } = useFormik({
    initialValues: settingValues,
    validationSchema: PrinterSettingsValidation,
  });
  const { handleToggle, canRenderField, handleResetChange } = useFormHandlers(
    setFieldValue,
    initialValues,
    entityID,
    entityType
  );

  const isReceiptLookupEnabled = useFeatureFlag("Receipt Lookup");
  const normalizedEntityType = entityType?.toLowerCase();
  const normalizedParentEntityType = parentEntityType?.toLowerCase();
  const isFacilityOrGroup =
    normalizedEntityType === "facility" ||
    normalizedEntityType === "facilitygroup";

  const belongsToFacilityGroup =
    normalizedEntityType === "facility" &&
    normalizedParentEntityType === "facilitygroup";
  const receiptPortalEntityId = belongsToFacilityGroup
    ? parentEntityID
    : entityID;

  const location = window.location;
  const port = location.port ? `:${location.port}` : "";
  const baseUrl = `${location.protocol}//${location.hostname}${port}`;
  const receiptPortalUrl = `${baseUrl}/receipts/${receiptPortalEntityId}`;

  const handleCopyReceiptUrl = async () => {
    try {
      await navigator.clipboard.writeText(receiptPortalUrl);
    } catch (err) {
      enqueueSnackbar("Failed to copy receipt portal URL", {
        variant: "error",
        tag: "FailedToCopyReceiptPortalUrl",
      });
    }
  };

  const togglePrinterExpanded = () => {
    setPrinterExpanded(!printerExpanded);
  };

  const handleReceiptLinesSubmit = async (newLines, fieldName) => {
    const setLinesFunction =
      fieldName.toLowerCase() === "footer"
        ? setReceiptFooterLines
        : setReceiptHeaderLines;
    setLinesFunction(newLines);
    if (
      !(await dispatchSetting(
        entityID,
        `receipt.${fieldName}`,
        newLines.join("\n")
      ))
    ) {
      enqueueSnackbar(`Failed to update receipt ${fieldName}`, {
        variant: "error",
        tag: "FailedToUpdateReceipt",
      });
    }
  };

  return (
    <Grid className={clsx("printing-panel", classes.panelRoot)} data-testid="printer-settings-form" container>
      <Accordion
        data-tag="printer"
        expanded={printerExpanded}
        TransitionProps={{
          unmountOnExit: true,
        }}
        className={clsx("accordion", classes.fullWidthAccordion)}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          onClick={togglePrinterExpanded}
        >
          <Typography className={clsx("heading", classes.heading)}>
            Receipt
          </Typography>
          <Typography
            className={clsx("secondary-heading", classes.secondaryHeading)}
          ></Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1}>
            {isReceiptLookupEnabled && isFacilityOrGroup && (
              <Grid
                data-tag="receipt-portal"
                className={clsx("receipt-portal", classes.receiptPortalSection)}
                item
                xs={12}
                container
                spacing={1}
                alignItems="flex-start"
              >
                <Grid item xs={12} sm={7} md={7} lg={7} xl={7}>
                  <div className={classes.receiptPortalField}>
                    <Typography
                      variant="body2"
                      component="span"
                      className={clsx(classes.mobilePayUrl, "receipt-portal-url")}
                    >
                      Facility Receipt Portal URL: {receiptPortalUrl}
                    </Typography>
                    <Tooltip title="Copy URL">
                      <IconButton
                        className={clsx(classes.receiptPortalCopyButton, "receipt-portal-copy-button")}
                        size="small"
                        color="primary"
                        aria-label="Copy receipt portal URL"
                        onClick={handleCopyReceiptUrl}
                      >
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={5}
                  md={5}
                  lg={5}
                  xl={5}
                  className={classes.receiptPortalQRColumn}
                >
                  <QRCode
                    value={receiptPortalUrl}
                    size={150}
                    role="img"
                    aria-label="Facility Receipt Portal QR Code"
                    className="receipt-portal-qr-code"
                  />
                </Grid>
              </Grid>
            )}
            <Grid
              data-tag="autoprintreceipt"
              className={clsx("auto-print-receipt")}
              item
              xs={12}
            >
              {canRenderField("autoprintreceipt") && (
                <>
                  <FormControlLabel
                    labelPlacement="end"
                    control={
                      <Switch
                        name="autoprintreceipt"
                        checked={values.autoprintreceipt}
                        data-checked={values.autoprintreceipt}
                        className={clsx("toggle")}
                        onChange={e => handleToggle(values, errors, e)}
                        color="primary"
                      />
                    }
                    label={<Typography>Auto Print Receipt</Typography>}
                  />
                  <SettingsResetButton
                    entityID={entityID}
                    entityType={entityType}
                    settingName="autoprintreceipt"
                    currentValue={values.autoprintreceipt}
                    originalValue={settingValuesParent.autoprintreceipt}
                    onClick={() => {
                      handleResetChange(
                        "autoprintreceipt",
                        settingValuesParent.autoprintreceipt
                      );
                    }}
                  />
                </>
              )}
            </Grid>
            <Grid
              data-tag="receipt.header"
              className={clsx("receipt-header")}
              item
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={6}
            >
              {canRenderField("receipt.header") && (
                <ReceiptLines
                  lines={receiptHeaderLines}
                  label="Header"
                  settingName="receipt.header"
                  onSubmit={async newLines => {
                    await handleReceiptLinesSubmit(newLines, "header");
                  }}
                  entityID={entityID}
                  entityType={entityType}
                  parentValueLines={settingValuesParent.receipt_header.split(
                    "\n"
                  )}
                />
              )}
            </Grid>
            <Grid
              data-tag="receipt.footer"
              className={clsx("receipt-footer")}
              item
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={6}
            >
              {canRenderField("receipt.footer") && (
                <ReceiptLines
                  lines={receiptFooterLines}
                  label="Footer"
                  settingName="receipt.footer"
                  onSubmit={async newLines => {
                    await handleReceiptLinesSubmit(newLines, "footer");
                  }}
                  entityID={entityID}
                  entityType={entityType}
                  parentValueLines={settingValuesParent.receipt_footer.split(
                    "\n"
                  )}
                />
              )}
            </Grid>
            {/* <Grid
              data-tag="printerretracttimeout"
              item
              xs={12}
              sm={6}
              md={6}
              lg={6}
              xl={6}
            >
              { canRenderField("printerretracttimeout") && (
                <TextField
                  variant="outlined"
                  label="Printer Retract Time-Out"
                  name="printerretracttimeout"
                  onChange={handleChange}
                  value={values.printerretracttimeout}
                  onBlur={(e) => handleInputChange(errors, e)}
                  error={Boolean(errors.printerretracttimeout)}
                  helperText={errors.printerretracttimeout || "in milliseconds"}
                />
              )}
            </Grid> */}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

PrinterSettingsForm.defaultProps = {
  settingValues: {},
};

export default PrinterSettingsForm;
